import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useCallback, useMemo } from 'react';
import i18n from 'i18next';
import { BrowserStorage } from 'utils/browserStorage';
import {
  Language,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  LANGUAGE_FLAGS,
} from '../constants';
import { getValidLanguage } from '../utils';
import dayjs from 'dayjs';
import {
  normalizeLanguageCode,
  findMatchingLanguageCode,
  getLanguageDisplayName,
} from '../services/languageService';
import Logger from 'utils/logger';

const isSupportedLanguage = (code: string): boolean =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(code);

const updateAppLanguage = async (language: Language): Promise<void> => {
  try {
    await i18n.changeLanguage(language);

    const normalizedCode = normalizeLanguageCode(language);

    if (!isSupportedLanguage(normalizedCode)) {
      Logger.warn('Unsupported language code, skipping dayjs locale load', {
        language,
        normalizedCode,
      });
      return;
    }

    try {
      const localeModule = await import(`dayjs/locale/${normalizedCode}.js`);
      dayjs.locale(localeModule.default);

      const weekStart = dayjs().localeData().firstDayOfWeek();
      dayjs.updateLocale(normalizedCode, { weekStart });
    } catch (dayjsError) {
      Logger.warn('Failed to load dayjs locale, using default', {
        language,
        normalizedCode,
        error: dayjsError,
      });
    }
  } catch (error) {
    if (language !== DEFAULT_LANGUAGE) {
      await updateAppLanguage(DEFAULT_LANGUAGE);
    }
  }
};

const useLanguageStore = create<{
  language: Language;
  setLanguage: (language: Language) => Promise<void>;
}>()(
  persist(
    (set) => ({
      language: DEFAULT_LANGUAGE,
      setLanguage: async (newLanguage: Language) => {
        await updateAppLanguage(newLanguage);
        set({ language: newLanguage });
      },
    }),
    {
      name: BrowserStorage.keys.Language,
      partialize: (state) => ({ language: state.language }),
    },
  ),
);

const getBrowserLanguage = (): string | null => {
  try {
    return navigator.language || null;
  } catch (error) {
    Logger.warn('Failed to detect browser language', { error });
    return null;
  }
};

const hasExplicitLanguagePreference = (): boolean => {
  const stored = BrowserStorage.get(BrowserStorage.keys.Language);
  return stored !== null;
};

export const initializeAppLanguage = async () => {
  if (!i18n.isInitialized) {
    await new Promise((resolve) => {
      const checkInitialized = () => {
        if (i18n.isInitialized) {
          resolve(void 0);
        } else {
          setTimeout(checkInitialized, 10);
        }
      };
      checkInitialized();
    });
  }

  const hasExplicitPreference = hasExplicitLanguagePreference();

  if (!hasExplicitPreference) {
    const browserLang = getBrowserLanguage();
    if (browserLang) {
      Logger.info('Detecting browser language for first-time user', {
        browserLanguage: browserLang,
      });

      const matchedLanguage = findMatchingLanguageCode(browserLang);
      if (matchedLanguage) {
        Logger.info('Auto-setting language based on browser preference', {
          browserLanguage: browserLang,
          matched: matchedLanguage,
        });
        await useLanguageStore.getState().setLanguage(matchedLanguage);
        return;
      } else {
        Logger.info('Browser language not available, using default', {
          browserLanguage: browserLang,
          fallback: DEFAULT_LANGUAGE,
        });
      }
    }
  }

  const storeState = useLanguageStore.getState();
  const savedLanguage = storeState.language;

  if (savedLanguage && savedLanguage !== i18n.language) {
    const matchedLanguage = findMatchingLanguageCode(savedLanguage);
    const languageToUse = matchedLanguage || savedLanguage;

    if (matchedLanguage && matchedLanguage !== savedLanguage) {
      Logger.info(
        'Mapped saved language to available variant during initialization',
        {
          saved: savedLanguage,
          matched: languageToUse,
        },
      );
    }

    await useLanguageStore.getState().setLanguage(languageToUse);
  }
};

const displayNameCache = new Map<string, string>();
SUPPORTED_LANGUAGES.forEach((key) => {
  displayNameCache.set(key, getLanguageDisplayName(key));
});

export const useAppLanguage = () => {
  const { language, setLanguage } = useLanguageStore();

  const changeLanguage = useCallback(
    async (newLang: string) => {
      const validLang = getValidLanguage(newLang, SUPPORTED_LANGUAGES);
      await setLanguage(validLang);
    },
    [setLanguage],
  );

  const languageOptions = useMemo(() => {
    return SUPPORTED_LANGUAGES.map((key) => ({
      key,
      name: displayNameCache.get(key) || key.toUpperCase(),
      flag: LANGUAGE_FLAGS[key] || '',
      isActive: language === key,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [language]);

  return {
    languageOptions,
    setLanguage: changeLanguage,
    currentLanguage: language,
  };
};

export default useAppLanguage;
